import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { 
  DollarSign, TrendingUp, PiggyBank, Users, RefreshCw
} from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";

const MESES_CURTO = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const ANOS = Array.from({ length: 25 }, (_, i) => 2026 + i);

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

const formatCurrencyShort = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);

const KPICard = ({ title, value, icon: Icon, color }) => (
  <Card className="card-hover">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="text-2xl lg:text-3xl font-bold number-display">{value}</p>
        </div>
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function DashboardPage() {
  const { authAxios } = useAuth();
  const [ano, setAno] = useState(2026);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async (showToast = false) => {
    if (showToast) setRefreshing(true);
    try {
      const res = await authAxios.get(`/monthly-detail/${ano}`);
      setDetail(res.data);
      if (showToast) toast.success("Dashboard atualizado!");
    } catch {
      toast.error("Erro ao carregar dashboard");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    const load = async () => {
      try {
        const res = await authAxios.get(`/monthly-detail/${ano}`);
        setDetail(res.data);
      } catch {
        toast.error("Erro ao carregar dashboard");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [ano, authAxios]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const meses = detail?.meses || [];
  const totals = meses.reduce((acc, m) => ({
    recebido: acc.recebido + (m.total_recebido || 0),
    investido: acc.investido + (m.total_investido || 0),
    lucro: acc.lucro + (m.lucro_bruto || 0),
    clientes: acc.clientes + (m.total_clientes || 0),
  }), { recebido: 0, investido: 0, lucro: 0, clientes: 0 });

  // Chart: comparativo mensal
  const comparativoData = meses.map((m, i) => ({
    name: MESES_CURTO[i],
    Recebido: m.total_recebido || 0,
    Investido: m.total_investido || 0,
    Lucro: m.lucro_bruto || 0,
  }));

  // Chart: evolucao lucro
  const lucroData = meses.map((m, i) => ({
    name: MESES_CURTO[i],
    Lucro: m.lucro_bruto || 0,
  }));

  // Chart: clientes mensal
  const clientesData = meses.map((m, i) => ({
    name: MESES_CURTO[i],
    Clientes: m.total_clientes || 0,
  }));

  // Pie: distribuicao anual
  const pieData = [
    { name: "Recebido", value: totals.recebido },
    { name: "Investido", value: totals.investido },
  ];
  const pieColors = ["#10b981", "#3b82f6"];

  const tooltipStyle = {
    backgroundColor: "hsl(var(--card))",
    border: "1px solid hsl(var(--border))",
    borderRadius: "8px",
    color: "#ffffff",
  };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Dashboard Anual - {ano}</h2>
          <p className="text-muted-foreground">Consolidado de janeiro a dezembro (base: Detalhamento)</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(ano)} onValueChange={(v) => setAno(Number(v))}>
            <SelectTrigger className="w-[100px]" data-testid="dashboard-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ANOS.map((a) => (
                <SelectItem key={a} value={String(a)}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => fetchData(true)}
            disabled={refreshing}
            data-testid="refresh-dashboard-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Atualizando..." : "Atualizar"}
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard title="Total Recebido" value={formatCurrency(totals.recebido)} icon={DollarSign} color="bg-emerald-500" />
        <KPICard title="Total Investido" value={formatCurrency(totals.investido)} icon={PiggyBank} color="bg-blue-500" />
        <KPICard title="Lucro Anual" value={formatCurrency(totals.lucro)} icon={TrendingUp} color="bg-amber-500" />
        <KPICard title="Total Clientes" value={totals.clientes} icon={Users} color="bg-purple-500" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Comparativo Mensal */}
        <Card>
          <CardHeader><CardTitle className="text-lg">Comparativo Mensal</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={comparativoData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => formatCurrencyShort(v)} />
                <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={tooltipStyle} labelStyle={{ color: "#fff" }} itemStyle={{ color: "#fff" }} />
                <Legend />
                <Bar dataKey="Recebido" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Investido" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Lucro" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Evolucao do Lucro */}
        <Card>
          <CardHeader><CardTitle className="text-lg">Evolucao do Lucro</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={lucroData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => formatCurrencyShort(v)} />
                <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={tooltipStyle} labelStyle={{ color: "#fff" }} itemStyle={{ color: "#fff" }} />
                <Line type="monotone" dataKey="Lucro" stroke="#f59e0b" strokeWidth={2} dot={{ fill: "#f59e0b", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Clientes por Mes */}
        <Card>
          <CardHeader><CardTitle className="text-lg">Clientes por Mes</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={clientesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "#fff" }} itemStyle={{ color: "#fff" }} />
                <Bar dataKey="Clientes" fill="#a855f7" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Distribuicao Anual */}
        <Card>
          <CardHeader><CardTitle className="text-lg">Distribuicao Anual</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={70} outerRadius={100} paddingAngle={5} dataKey="value">
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={pieColors[i]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={tooltipStyle} labelStyle={{ color: "#fff" }} itemStyle={{ color: "#fff" }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
